import { Disclosure, Transition } from '@headlessui/react';
import React from 'react';
import InputFactory from '../components/inputs/InputFactory';
import ToggleInput from '../components/inputs/ToggleInput';
import { OTHER_PERSON } from '../utils/constants';
import FormLabel from '../components/basicComponents/FormLabel';
import Attachments from '../components/inputs/Attachments';
import { getText } from '../utils/functions';

const defaultTransition = {
	enter: 'transition duration-1000 ease-in-out',
	enterFrom: 'transform opacity-0',
	enterTo: 'transform opacity-100',
	leave: 'transition duration-600 ease-in-out',
	leaveFrom: 'transform opacity-100',
	leaveTo: 'transform opacity-0',
};

function RepresentativeOrDelegateForm({
	colors,
	portal,
	formData,
	formType,
	personType,
	setFormData = () => { },
	translate,

	/* Optional props */
	isOptional,
	styleLIST,
	styleTransition,
	classNameDIV = 'space-y-5',
	classNameCUSTOMS = 'w-full md:pl-8 mt-8 grid grid-flow-row lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-10 items-end',
	classNameDEFAULTS = 'w-full flex gap-y-5 gap-x-2 md:gap-x-10 md:gap-y-5 md:pl-4 mt-4',
	classNameBTN = 'flex font-lato space-x-4 items-center w-full',
	classNameCUSTOMDIV = 'space-y-10',
}) {
	const delegate = 'delegate';
	const inputProps = {
		confirmError: getText(portal?.config?.validation?.confirmError, translate)
	};
	/* Show default variable */
	const [defaultValues, setDefaultValue] = React.useState();
	const [inputConfig, setInputConfig] = React.useState({ name: 'name' });

	/* Default values for the styles */
	const defaultStyleBTN = {
		color: colors?.text,
	};

	/* This allows for only one of the fields to be modified and still keep the other constant values */
	const overrideTransition = { ...defaultTransition, ...styleTransition };

	/* Get the dinamic inputs */
	const dinamicInputs = React.useMemo(() => portal?.config?.inputs?.[formType]?.[personType]
		?.filter(input => input.isActive), [portal?.config?.inputs?.[formType]?.[personType]]);

	const hasDefault = React.useMemo(
		() => portal?.config?.predefinedPeople?.[personType]?.[formType].length > 0,
		[portal?.config?.predefinedPeople?.[personType]?.[formType]]
	);

	/* Get the default delegates config */
	const getInputConfig = React.useCallback(() => {
		setDefaultValue(
			portal?.config?.predefinedPeople?.[personType]?.[formType]?.map(item => item.value)
		);
		return {
			name: 'name',
			label: '',
			text: {
				es: '',
				en: '',
				gal: '',
				cat: '',
				eu: '',
				fr: '',
				pt: '',
				pl: '',
			},
			type: 'radio',
			value: '',
			lastDefault: true,
			showValidation: false,
			items: [
				...portal?.config?.predefinedPeople?.[personType]?.[formType],
				(personType === delegate ?
					portal?.config?.forms?.delegationOtherPerson
					: portal?.config?.forms?.representationOtherPerson
				) || OTHER_PERSON
			],
			isActive: true,
			confirm: false,
			checkbbdd: false,
			required: true,
		};
	}, [portal?.config?.predefinedPeople?.[personType]?.[formType]]);

	React.useEffect(() => {
		setInputConfig(getInputConfig());
	}, [getInputConfig]);

	const openOptionalDelegate = () => {
		setFormData({
			...formData,
			[personType]: personType === delegate ?
				{
					name: '',
					surname: '',
					dni: '',
					email: '',
					phone: '',
				} :
				{
					name: '',
					surname: '',
					dni: '',
					email: '',
					phone: '',
					language: 'es',
				}
		});
	};

	if (isOptional) {
		return <div>
			<Disclosure as='div' className={classNameDIV}>
				{({ open }) => (
					<>
						{/* DELEGATE TITLE */}
						<div className={classNameBTN} style={defaultStyleBTN}>
							<FormLabel
								text={personType === delegate ?
									portal?.config?.forms?.delegation
									: portal?.config?.forms?.representativeData
								}
								colors={colors}
								translate={translate}
							/>
							<Disclosure.Button as='div'>
								<ToggleInput
									value={open}
									colors={colors}
									onChange={() => openOptionalDelegate()} />
							</Disclosure.Button>
						</div>
						<Transition
							enter={overrideTransition.enter}
							enterFrom={overrideTransition.enterFrom}
							enterTo={overrideTransition.enterTo}
							leave={overrideTransition.leave}
							leaveFrom={overrideTransition.leaveFrom}
							leaveTo={overrideTransition.leaveTo}
						>
							<Disclosure.Panel>
								<div>
									{/* PLATFORM OR EDITOR DELEGATES */}
									{hasDefault
										&& <div className={classNameDEFAULTS}>
											<InputFactory
												portal={portal}
												showLabel={false}
												inputConfig={inputConfig}
												personType={personType}
												value={formData[personType][inputConfig.name]}
												formData={formData}
												setFormData={setFormData}
												colors={colors}
												translate={translate}
												inputProps={inputProps}
											/>
										</div>}
									{/* DELEGATE CUSTOM OPTION */}
									{(!hasDefault || !defaultValues?.includes(formData?.[personType].name))
										&& <div className={classNameCUSTOMDIV}>
											<div className={classNameCUSTOMS} style={styleLIST}>
												{dinamicInputs
													&& dinamicInputs?.map(inputConfig2 => <InputFactory
														portal={portal}
														inputConfig={inputConfig2}
														formData={formData}
														setFormData={setFormData}
														value={formData[personType][inputConfig2.name]}
														personType={personType}
														key={inputConfig2.name}
														colors={colors}
														translate={translate}
														inputProps={inputProps}
													/>)}
											</div>
											{/* ATTACHMENTS */}
											<Attachments
												portal={portal}
												personType={personType}
												formType={formType}
												translate={translate}
												formData={formData}
												setFormData={setFormData}
												colors={colors}
											/>
										</div>
									}
								</div>
							</Disclosure.Panel>
						</Transition>
					</>
				)}
			</Disclosure>
		</div>;
	}

	if (portal?.name !== 'mutualmedica') {
		return <div>
			{/* DELEGATE TITLE */}
			<FormLabel
				text={personType === delegate ?
					portal?.config?.forms?.delegation
					: portal?.config?.forms?.representativeData
				}
				colors={colors}
				translate={translate}
			/>
			{/* PLATFORM OR EDITOR DELEGATES */}
			{hasDefault
				&& <div className={classNameDEFAULTS}>
					<InputFactory
						portal={portal}
						showLabel={false}
						inputConfig={inputConfig}
						value={formData[personType].name}
						personType={personType}
						formData={formData}
						setFormData={setFormData}
						colors={colors}
						translate={translate}
						inputProps={inputProps}
					/>
				</div>}
			{/* DELEGATE CUSTOM OPTION */}
			{(!hasDefault || !defaultValues?.includes(formData?.[personType].name))
				&& <div className={classNameCUSTOMDIV}>
					<div className={classNameCUSTOMS} style={styleLIST}>
						{dinamicInputs
							&& dinamicInputs?.map(inputConfig2 => <InputFactory
								portal={portal}
								inputConfig={inputConfig2}
								formData={formData}
								setFormData={setFormData}
								personType={personType}
								value={formData[personType][inputConfig2.name]}
								key={inputConfig2.name}
								colors={colors}
								translate={translate}
								inputProps={inputProps}
							/>)}
					</div>
					{/* ATTACHMENTS */}
					<Attachments
						portal={portal}
						personType={personType}
						formType={formType}
						translate={translate}
						formData={formData}
						setFormData={setFormData}
						colors={colors}
					/>
				</div>
			}
		</div>;
	}
	return '';
}

export default RepresentativeOrDelegateForm;
